import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useResumenMes } from '@/hooks/useResumenMes';
import type { FiltroVenta } from '@/lib/filtroVenta';
import { cn } from '@/lib/utils';
import type { SemanaOperativa } from '@adelante/shared';
import { CircleNotch } from '@phosphor-icons/react';

/**
 * Resumen del Mes — Financiero: por cada semana operativa del MISMO MES que la
 * seleccionada, muestra la PRODUCCIÓN de la semana (Directo, Indirecto, Venta,
 * Utilidad en ₡) + el total acumulado del mes a la fecha. Reusa useResumenMes
 * (caché compartida con el reporte abierto).
 */
export function ResumenMesFinancieroVista({
  semanas,
  semanaSel,
  filtroVenta,
}: {
  semanas: SemanaOperativa[];
  semanaSel: number | null;
  filtroVenta: FiltroVenta;
}) {
  const { filas, totFin, cargando } = useResumenMes(semanas, semanaSel, filtroVenta);

  return (
    <Card className="overflow-hidden print:break-inside-avoid print:shadow-none">
      <div className="flex items-center gap-2 border-b border-border bg-zinc-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Resumen del mes — Financiero (producción por semana)
        {cargando && (
          <CircleNotch className="h-3 w-3 animate-spin" weight="bold" aria-hidden="true" />
        )}
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Semana</TableHead>
            <TableHead>Período</TableHead>
            <TableHead className="text-right">Directo</TableHead>
            <TableHead className="text-right">Indirecto</TableHead>
            <TableHead className="text-right">Venta</TableHead>
            <TableHead className="text-right">Utilidad</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filas.map((f) => {
            const esSel = Number(f.semana.id) === semanaSel;
            return (
              <TableRow
                key={f.semana.id}
                className={cn(esSel && 'bg-adelante-primary/20 font-medium')}
              >
                <TableCell
                  className={cn(
                    'font-mono text-xs font-semibold',
                    esSel && 'border-l-4 border-adelante-primary text-adelante-primary-dark',
                  )}
                >
                  S{f.semana.numero_semana}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {periodo(f.semana.fecha_inicio)} → {periodo(f.semana.fecha_fin)}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums text-sm">
                  {f.baseSemanal ? fmtMonto(f.directo) : '—'}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums text-sm">
                  {f.baseSemanal ? fmtMonto(f.indirecto) : '—'}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums text-sm">
                  {f.baseSemanal ? fmtMonto(f.venta) : '—'}
                </TableCell>
                <TableCell
                  className={cn(
                    'text-right font-mono tabular-nums text-sm font-semibold',
                    !f.baseSemanal
                      ? 'text-muted-foreground'
                      : f.utilidad >= 0
                        ? 'text-adelante-primary-dark'
                        : 'text-red-600',
                  )}
                >
                  {f.baseSemanal ? fmtMonto(f.utilidad) : '—'}
                </TableCell>
              </TableRow>
            );
          })}
          {filas.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                Sin semanas en este mes.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow className="border-t-2">
            <TableCell className="font-semibold" colSpan={2}>
              Total a la fecha
            </TableCell>
            <TableCell className="text-right font-mono tabular-nums text-xs font-semibold">
              {fmtMonto(totFin.directo)}
            </TableCell>
            <TableCell className="text-right font-mono tabular-nums text-xs font-semibold">
              {fmtMonto(totFin.indirecto)}
            </TableCell>
            <TableCell className="text-right font-mono tabular-nums text-xs font-semibold">
              {fmtMonto(totFin.venta)}
            </TableCell>
            <TableCell
              className={cn(
                'text-right font-mono tabular-nums text-xs font-semibold',
                totFin.utilidad >= 0 ? 'text-adelante-primary-dark' : 'text-red-600',
              )}
            >
              {fmtMonto(totFin.utilidad)}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </Card>
  );
}

/** ₡ compacto (₡5.8M, ₡320k) para celdas de tabla. */
function fmtMonto(n: number): string {
  if (!n) return '—';
  if (Math.abs(n) >= 1_000_000)
    return `₡${(n / 1_000_000).toLocaleString('en-US', { maximumFractionDigits: 1 })}M`;
  if (Math.abs(n) >= 1_000)
    return `₡${(n / 1_000).toLocaleString('en-US', { maximumFractionDigits: 0 })}k`;
  return `₡${n.toLocaleString('en-US')}`;
}

/** dd/mm desde un ISO YYYY-MM-DD. */
function periodo(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-');
  return `${d}/${m}`;
}
